import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import ArticleToc from "@/pages/article/components/ArticleToc";
import ArticleRelated from "@/pages/article/components/ArticleRelated";
import { getArticleBySlug, getRelatedArticles, formatViews } from "@/utils/content";
import usePageMeta from "@/hooks/usePageMeta";

const SITE_ORIGIN = "https://glmrz.cn";

export default function ArticlePage() {
  const { slug = "" } = useParams();
  const article = getArticleBySlug(slug);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState("");
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  const tocItems = useMemo(
    () =>
      (article?.content ?? [])
        .map((block, idx) => ({ block, idx }))
        .filter(({ block }) => block.type === "h2")
        .map(({ block, idx }) => ({ id: `sec-${idx}`, text: block.text })),
    [article],
  );

  const related = useMemo(
    () => (article ? getRelatedArticles(article, 3) : []),
    [article],
  );

  usePageMeta({
    title: article ? `${article.title} - 黑料八卦` : "内容不存在 - 黑料八卦",
    description: article
      ? article.excerpt
      : "该幕后内容可能已被移动或删除，请返回首页浏览其他综艺、影视与剧组幕后故事。",
    keywords: article
      ? `黑料八卦,${article.categoryName},${article.tags.join(",")},拍摄现场,剧组幕后`
      : "黑料八卦,幕后内容",
    canonicalPath: `/article/${slug}`,
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setLiked(false);
    setLikeCount(0);
  }, [slug]);

  useEffect(() => {
    const onScroll = () => {
      const el = bodyRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight * 0.6;
      const passed = Math.min(Math.max(-rect.top + 120, 0), total > 0 ? total : 1);
      setProgress(total > 0 ? Math.round((passed / total) * 100) : 0);

      let current = "";
      tocItems.forEach((item) => {
        const heading = document.getElementById(item.id);
        if (heading && heading.getBoundingClientRect().top <= 160) {
          current = item.id;
        }
      });
      setActiveId(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [tocItems]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="max-w-portal mx-auto px-4 md:px-6 py-24 text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-400" />
          <h1 className="font-heading text-2xl font-black text-foreground-950 mt-4">
            没有找到这篇幕后内容
          </h1>
          <p className="text-sm text-foreground-600 mt-3">
            它可能已经被移动或链接有误，回到首页看看最新的现场报道吧。
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 mt-6 px-6 py-3 rounded-md bg-foreground-950 text-background-50 text-sm font-bold whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        datePublished: article.date,
        dateModified: article.date,
        inLanguage: "zh-CN",
        articleSection: article.categoryName,
        keywords: article.tags.join(","),
        author: { "@type": "Person", name: article.author },
        publisher: { "@type": "Organization", name: "黑料八卦" },
        mainEntityOfPage: `${SITE_ORIGIN}/article/${article.slug}`,
        image: article.cover,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
          {
            "@type": "ListItem",
            position: 2,
            name: article.categoryName,
            item: `${SITE_ORIGIN}/category/${article.categorySlug}`,
          },
          { "@type": "ListItem", position: 3, name: article.title },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <SiteHeader />

      {/* 阅读进度 */}
      <div className="sticky top-[100px] z-40 h-0.5 bg-background-200">
        <div
          className="h-full bg-accent-500 transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>

      <main className="max-w-portal mx-auto px-4 md:px-6 pt-8">
        <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500 mb-6">
          <Link to="/" className="hover:text-accent-700 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <Link
            to={`/category/${article.categorySlug}`}
            className="hover:text-accent-700 transition-colors cursor-pointer"
          >
            {article.categoryName}
          </Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <span className="text-foreground-700 line-clamp-1 max-w-[60%]">{article.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          {/* 正文 */}
          <article className="lg:col-span-2" ref={bodyRef}>
            <header className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="text-xs font-bold px-2.5 py-1 rounded bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer"
                >
                  {article.categoryName}
                </Link>
                {article.isHot ? (
                  <span className="text-xs font-bold px-2.5 py-1 rounded bg-accent-500 text-background-50">
                    现场热帖
                  </span>
                ) : null}
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed mt-4">{article.excerpt}</p>
              <div className="flex flex-wrap items-center gap-4 mt-5 pb-5 border-b border-background-200 text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center" />
                  {article.author}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center" />
                  {article.date}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-time-line w-4 h-4 flex items-center justify-center" />
                  {article.readTime}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center" />
                  {formatViews(article.views)} 阅读
                </span>
              </div>
            </header>

            <figure className="mb-8">
              <div className="w-full h-56 md:h-80 overflow-hidden rounded-lg bg-background-200">
                <img
                  src={article.cover}
                  alt={`${article.title} - ${article.categoryName}幕后报道封面图`}
                  title={`${article.title} 现场照片`}
                  className="w-full h-full object-top object-cover"
                />
              </div>
              <figcaption className="text-xs text-foreground-500 mt-2 text-center">
                图 · {article.categoryName}现场记录（示意画面）
              </figcaption>
            </figure>

            <div className="prose-body">
              {article.content.map((block, idx) => {
                if (block.type === "h2") {
                  return (
                    <h2 key={idx} id={`sec-${idx}`} className="scroll-mt-32">
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === "quote") {
                  return (
                    <blockquote
                      key={idx}
                      className="my-6 pl-5 border-l-4 border-primary-500 bg-primary-50/60 py-3 pr-4 rounded-r-md"
                    >
                      <p className="text-foreground-800 italic font-heading text-base md:text-lg !mb-0">
                        「{block.text}」
                      </p>
                    </blockquote>
                  );
                }
                return <p key={idx}>{block.text}</p>;
              })}
            </div>

            {/* 标签 */}
            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-xs font-medium text-foreground-700"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* 互动 */}
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <button
                type="button"
                onClick={() => {
                  setLiked((v) => !v);
                  setLikeCount((c) => (liked ? Math.max(c - 1, 0) : c + 1));
                }}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md border-2 text-sm font-bold whitespace-nowrap cursor-pointer transition-colors ${
                  liked
                    ? "bg-accent-500 border-accent-500 text-background-50"
                    : "border-foreground-950 text-foreground-950 hover:bg-foreground-950 hover:text-background-50"
                }`}
              >
                <i className={`${liked ? "ri-heart-3-fill" : "ri-heart-3-line"} w-4 h-4 flex items-center justify-center`} />
                这瓜有意思 {likeCount > 0 ? likeCount : ""}
              </button>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(`${SITE_ORIGIN}/article/${article.slug}`);
                }}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm font-bold text-foreground-800 whitespace-nowrap cursor-pointer hover:border-primary-500 transition-colors"
              >
                <i className="ri-link w-4 h-4 flex items-center justify-center" />
                复制链接
              </button>
            </div>

            {/* 作者 */}
            <div className="flex items-start gap-4 mt-8 p-5 rounded-lg bg-background-100 border border-background-200">
              <span className="w-12 h-12 shrink-0 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
                <i className="ri-user-star-line text-2xl" />
              </span>
              <div>
                <p className="font-heading text-sm font-bold text-foreground-950">
                  {article.author} · 幕后编辑
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mt-1">
                  长期跟组与探班，关注现场调度、剪辑取舍与剧组协作方式，习惯把制作过程讲成人话。
                </p>
              </div>
            </div>
          </article>

          {/* 侧栏 */}
          <aside className="space-y-6">
            <div className="lg:sticky lg:top-32 space-y-6">
              <ArticleToc items={tocItems} activeId={activeId} />

              <div className="rounded-lg bg-secondary-900 text-background-100 p-5">
                <h2 className="font-heading text-base font-bold mb-3">关于幕后内容</h2>
                <p className="text-sm text-background-300 leading-relaxed">
                  本文基于公开的制作访谈与主创分享整理，涉及具体作品时以已公开信息为准，
                  不揣测未公开的私人事务。
                </p>
                <Link
                  to="/about#principles"
                  className="inline-flex items-center gap-1.5 mt-4 text-sm font-bold text-primary-300 hover:text-primary-200 transition-colors cursor-pointer"
                >
                  查看内容原则
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </Link>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">
                  幕后备忘
                </h2>
                <ul className="space-y-3 text-sm text-foreground-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line w-4 h-4 mt-0.5 shrink-0 flex items-center justify-center text-accent-600" />
                    现场调度往往比表演更早开始
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line w-4 h-4 mt-0.5 shrink-0 flex items-center justify-center text-accent-600" />
                    被删的戏不一定是不好看
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line w-4 h-4 mt-0.5 shrink-0 flex items-center justify-center text-accent-600" />
                    一份通告单能看出一部戏的节奏
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <ArticleRelated articles={related} />
      <SiteFooter />
      <BackToTop />
    </div>
  );
}